// Toast notification module
let toastContainer = null;
const activeToasts = new Map();
let toastIdCounter = 0;

export function initToast(elements) {
    toastContainer = elements.toastContainer || document.getElementById("toastContainer");
}

/**
 * Show a toast notification
 * @param {Object} options - Toast options
 * @param {string} options.title - Toast title
 * @param {string} options.message - Toast message
 * @param {string} options.type - Toast type: 'loading', 'success', 'error', 'info'
 * @param {number} options.duration - Auto-dismiss duration in ms (0 for no auto-dismiss)
 * @param {string} options.id - Optional custom ID for updating the toast later
 * @returns {string} Toast ID for updating/dismissing later
 */
/**
 * @param {{ title?: string, message?: string, type?: string, duration?: number, id?: string | null }} options
 * @returns {string | null} the id of the toast, so it can be updated or dismissed
 */
export function showToast({ title, message, type = "info", duration = 0, id = null }) {
    if (!toastContainer) {
        console.warn("Toast container not initialized");
        return null;
    }

    const toastId = id || `toast-${++toastIdCounter}`;

    // Check if we're updating an existing toast
    if (activeToasts.has(toastId)) {
        updateToast(toastId, { title, message, type, duration });
        return toastId;
    }

    const toast = document.createElement("div");
    toast.className = `toast toast-${type}`;
    toast.dataset.toastId = toastId;

    const iconHtml = type === "loading"
        ? '<div class="toast-spinner"></div>'
        : '<div class="toast-icon"></div>';

    toast.innerHTML = `
        ${iconHtml}
        <div class="toast-content">
            <div class="toast-title">${escapeHtml(title)}</div>
            <div class="toast-message">${escapeHtml(message)}</div>
        </div>
        <button type="button" class="toast-close" aria-label="Dismiss">&times;</button>
    `;

    // Close button handler
    toast.querySelector(".toast-close").addEventListener("click", () => {
        dismissToast(toastId);
    });

    toastContainer.appendChild(toast);

    const toastData = { element: toast, timeoutId: null };

    // Set up auto-dismiss if duration is specified
    if (duration > 0) {
        toastData.timeoutId = setTimeout(() => {
            dismissToast(toastId);
        }, duration);
    }

    activeToasts.set(toastId, toastData);
    return toastId;
}

/**
 * Update an existing toast
 * @param {string} toastId - Toast ID to update
 * @param {Object} options - New toast options
 */
export function updateToast(toastId, { title, message, type, duration }) {
    const toastData = activeToasts.get(toastId);
    if (!toastData) return;

    const toast = toastData.element;

    // Update content
    if (title !== undefined) {
        toast.querySelector(".toast-title").textContent = title;
    }
    if (message !== undefined) {
        toast.querySelector(".toast-message").textContent = message;
    }

    // Update type/styling and icon
    if (type !== undefined) {
        const oldType = toast.className.match(/toast-(\w+)/)?.[1];
        toast.className = `toast toast-${type}`;

        // Replace spinner with icon or vice versa
        if (oldType === "loading" && type !== "loading") {
            const spinner = toast.querySelector(".toast-spinner");
            if (spinner) {
                const icon = document.createElement("div");
                icon.className = "toast-icon";
                spinner.replaceWith(icon);
            }
        } else if (oldType !== "loading" && type === "loading") {
            const icon = toast.querySelector(".toast-icon");
            if (icon) {
                const spinner = document.createElement("div");
                spinner.className = "toast-spinner";
                icon.replaceWith(spinner);
            }
        }
    }

    // Clear existing timeout
    if (toastData.timeoutId) {
        clearTimeout(toastData.timeoutId);
        toastData.timeoutId = null;
    }

    // Set up new auto-dismiss if duration is specified
    if (duration > 0) {
        toastData.timeoutId = setTimeout(() => {
            dismissToast(toastId);
        }, duration);
    }
}

/**
 * Dismiss a toast notification
 * @param {string} toastId - Toast ID to dismiss
 */
export function dismissToast(toastId) {
    const toastData = activeToasts.get(toastId);
    if (!toastData) return;

    const toast = toastData.element;

    // Clear any pending timeout
    if (toastData.timeoutId) {
        clearTimeout(toastData.timeoutId);
    }

    // Add hiding animation class
    toast.classList.add("toast-hiding");

    // Remove after animation completes
    toast.addEventListener("animationend", () => {
        toast.remove();
        activeToasts.delete(toastId);
    }, { once: true });
}

/**
 * Dismiss all toasts
 */
export function dismissAllToasts() {
    activeToasts.forEach((_, toastId) => {
        dismissToast(toastId);
    });
}

/**
 * Show a loading toast for an import operation
 * @param {string} platform - Platform name (e.g., "Lichess")
 * @param {string} id - Import ID for tracking
 * @returns {string} Toast ID
 */
export function showImportToast(platform, id) {
    return showToast({
        id: `import-${id}`,
        title: `Importing from ${platform}`,
        message: "Starting import...",
        type: "loading",
    });
}

/**
 * Update an import toast with progress
 * @param {string} id - Import ID
 * @param {string} message - Progress message
 */
export function updateImportProgress(id, message) {
    updateToast(`import-${id}`, {
        message,
    });
}

/**
 * Mark an import toast as completed
 * @param {string} id - Import ID
 * @param {string} message - Completion message
 * @param {boolean} success - Whether the import was successful
 */
export function completeImportToast(id, message, success = true) {
    updateToast(`import-${id}`, {
        title: success ? "Import Complete" : "Import Failed",
        message,
        type: success ? "success" : "error",
        duration: 5000, // Auto-dismiss after 5 seconds
    });
}

function escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
}
